import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import {
  Box,
  Collapse,
  Container,
  Dialog,
  IconButton,
  Typography,
  type SxProps,
  type Theme,
} from '@mui/material';
import { Fragment, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { SiteLayout } from '../components/SiteLayout';
import { FeedbackDialog } from '../components/feedback/FeedbackDialog';
import RELEASES, {
  type Release,
  type ReleaseScreenshot,
  type ReleaseSection,
} from '../data/releases';
import { colors } from '../theme';

const CREAM = colors.brandCream;
const GOLD = colors.goldDark; // #bb8c3f — for text / borders (good contrast)
const GOLD_LIGHT = colors.goldLight; // #f4bc58 — lighter end of gold gradients
// Per-area accent for the release tag, so "which part" reads at a glance.
const AREA_TONE: Record<string, string> = {
  Depletions: '#0d9488',
  Sales: '#4f46e5',
  Marketing: '#7c3aed',
  'Ask ABHI': '#7c20f4',
};
const areaTone = (area: string): string => AREA_TONE[area] ?? colors.chartPrimary;
const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

// Parse YYYY-MM-DD manually so the day is stable across timezones (the
// bare Date constructor treats it as UTC midnight).
function formatReleaseDate(iso: string): string {
  const [year, month, day] = iso.split('-').map(Number);
  if (!year || !month || !day) return iso;
  return `${MONTH_NAMES[month - 1]} ${day}, ${year}`;
}

// ---- tiny inline markdown: **bold** and `code` ------------------------
const INLINE_RE = /(\*\*[^*]+\*\*|`[^`]+`)/g;
function renderInline(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  INLINE_RE.lastIndex = 0;
  let key = 0;
  while ((m = INLINE_RE.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith('**')) {
      out.push(
        <Box key={key++} component="strong" sx={{ fontWeight: 700, color: colors.textPrimary }}>
          {tok.slice(2, -2)}
        </Box>,
      );
    } else {
      out.push(
        <Box
          key={key++}
          component="code"
          sx={{
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize: '0.85em',
            px: 0.5,
            py: 0.1,
            borderRadius: 0.75,
            bgcolor: colors.surfaceSubtle,
            border: `1px solid ${colors.borderLight}`,
          }}
        >
          {tok.slice(1, -1)}
        </Box>,
      );
    }
    last = INLINE_RE.lastIndex;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

// ---- block-level: paragraphs + "- " bullets ---------------------------
function renderBody(body: string): ReactNode {
  const lines = body.split('\n');
  const blocks: ReactNode[] = [];
  let para: string[] = [];
  let bullets: string[] = [];
  let key = 0;

  const flushPara = () => {
    if (para.length) {
      blocks.push(
        <Typography
          key={key++}
          sx={{ fontSize: 13.5, color: colors.textSecondary, lineHeight: 1.65, mb: 0.5 }}
        >
          {renderInline(para.join(' '))}
        </Typography>,
      );
      para = [];
    }
  };
  const flushBullets = () => {
    if (bullets.length) {
      blocks.push(
        <Box key={key++} component="ul" sx={{ m: 0, mb: 0.75, pl: 2.5 }}>
          {bullets.map((b, i) => (
            <Box
              key={i}
              component="li"
              sx={{ fontSize: 13.5, color: colors.textSecondary, lineHeight: 1.6, mb: 0.35 }}
            >
              {renderInline(b)}
            </Box>
          ))}
        </Box>,
      );
      bullets = [];
    }
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    if (line.trim() === '') {
      flushBullets();
      flushPara();
    } else if (line.trimStart().startsWith('- ')) {
      flushPara();
      bullets.push(line.trimStart().slice(2));
    } else {
      flushBullets();
      para.push(line);
    }
  }
  flushBullets();
  flushPara();
  return <>{blocks}</>;
}

// ---- screenshot (framed, click-to-zoom, hide if it fails to load) -----
function screenshotsOf(section: ReleaseSection): ReleaseScreenshot[] {
  const items: ReleaseScreenshot[] = [];
  if (section.screenshot) items.push({ src: section.screenshot });
  for (const s of section.screenshots ?? []) items.push(s);
  return items;
}

function Shot({ shot, onZoom }: { shot: ReleaseScreenshot; onZoom: (src: string) => void }) {
  const [broken, setBroken] = useState(false);
  if (broken) return null;
  return (
    <Box
      onClick={() => onZoom(shot.src)}
      sx={{
        position: 'relative',
        borderRadius: 2,
        overflow: 'hidden',
        border: `1px solid ${colors.border}`,
        boxShadow: '0 8px 28px -14px rgba(15,23,42,0.35)',
        cursor: 'zoom-in',
        transition: 'transform 0.15s, box-shadow 0.15s',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 14px 36px -14px rgba(15,23,42,0.45)',
        },
        '&:hover .zoom-badge': { opacity: 1 },
      }}
    >
      <Box
        component="img"
        src={shot.src}
        alt={shot.label ?? 'Screenshot'}
        onError={() => setBroken(true)}
        sx={{ display: 'block', width: '100%', height: 'auto' }}
      />
      <Box
        className="zoom-badge"
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          px: 0.75,
          py: 0.35,
          borderRadius: 1,
          bgcolor: 'rgba(15,23,42,0.72)',
          color: '#fff',
          fontSize: 11,
          fontWeight: 700,
          opacity: 0,
          transition: 'opacity 0.15s',
        }}
      >
        <ZoomInIcon sx={{ fontSize: 14 }} /> Zoom
      </Box>
      {shot.label && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 8,
            left: 8,
            px: 0.75,
            py: 0.3,
            borderRadius: 1,
            bgcolor: 'rgba(15,23,42,0.72)',
            color: '#fff',
            fontSize: 11,
            fontWeight: 700,
          }}
        >
          {shot.label}
        </Box>
      )}
    </Box>
  );
}

const cardSx: SxProps<Theme> = {
  position: 'relative',
  overflow: 'hidden',
  p: { xs: 2.75, md: 4 },
  pt: { xs: 3, md: 4.25 },
  borderRadius: 3.5,
  border: `1px solid ${GOLD}22`,
  bgcolor: '#fffdf9',
  boxShadow: '0 1px 2px rgba(45,45,44,0.04), 0 18px 40px -24px rgba(45,45,44,0.22)',
  transition: 'box-shadow 0.2s, border-color 0.2s',
  // Gold gradient bar across the top edge — the premium accent.
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 4,
    background: `linear-gradient(90deg, ${GOLD_LIGHT}, ${GOLD} 55%, ${colors.brandDark}55)`,
  },
  '&:hover': {
    borderColor: `${GOLD}55`,
    boxShadow: '0 2px 4px rgba(45,45,44,0.05), 0 24px 50px -24px rgba(45,45,44,0.30)',
  },
};

function Sep() {
  return (
    <Box component="span" sx={{ color: colors.textMuted, opacity: 0.55, fontWeight: 400 }}>
      ·
    </Box>
  );
}

function StatChip({ children, active = false }: { children: ReactNode; active?: boolean }) {
  return (
    <Box
      sx={{
        px: 1.5,
        py: 0.7,
        borderRadius: 999,
        fontSize: 12.5,
        fontWeight: 700,
        whiteSpace: 'nowrap',
        ...(active
          ? {
              color: '#fff',
              background: `linear-gradient(140deg, ${GOLD_LIGHT}, ${GOLD})`,
              boxShadow: `0 3px 10px ${GOLD}44`,
            }
          : {
              color: colors.textSecondary,
              bgcolor: '#fff',
              border: `1px solid ${colors.border}`,
            }),
      }}
    >
      {children}
    </Box>
  );
}

function ReleaseCard({
  release,
  onZoom,
  isLatest = false,
  defaultOpen = false,
}: {
  release: Release;
  onZoom: (src: string) => void;
  isLatest?: boolean;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const featureCount = release.sections.length;
  return (
    <Box sx={cardSx}>
      {/* Eyebrow row — dot · month · No. · date  ·  Latest badge */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          mb: 1.25,
          flexWrap: 'wrap',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.9,
            fontSize: 11.5,
            fontWeight: 800,
            letterSpacing: '0.09em',
            textTransform: 'uppercase',
            color: GOLD,
            flexWrap: 'wrap',
          }}
        >
          {release.area && (
            <Box
              sx={{
                px: 0.85,
                py: 0.25,
                borderRadius: 1,
                fontSize: 10,
                fontWeight: 800,
                letterSpacing: '0.09em',
                color: areaTone(release.area),
                bgcolor: `${areaTone(release.area)}14`,
                border: `1px solid ${areaTone(release.area)}44`,
              }}
            >
              {release.area}
            </Box>
          )}
          <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: GOLD }} />
          {release.version}
          <Sep />
          <Box component="span" sx={{ color: colors.textMuted }}>
            {formatReleaseDate(release.date)}
          </Box>
        </Box>
        {isLatest && (
          <Box
            sx={{
              px: 1,
              py: 0.4,
              borderRadius: 999,
              fontSize: 11,
              fontWeight: 700,
              color: colors.success,
              bgcolor: `${colors.success}14`,
              border: `1px solid ${colors.success}40`,
              whiteSpace: 'nowrap',
            }}
          >
            Latest release
          </Box>
        )}
      </Box>

      {/* Title — big, clean sans. */}
      {release.title && (
        <Typography
          sx={{
            fontSize: { xs: 21, md: 24 },
            fontWeight: 800,
            color: colors.brandDark,
            letterSpacing: '-0.01em',
            mb: 1.5,
            lineHeight: 1.2,
          }}
        >
          {release.title}
        </Typography>
      )}

      {/* Blockquote summary — italic, gold left rule. */}
      <Box sx={{ borderLeft: `3px solid ${GOLD}`, pl: 2, mb: 2 }}>
        <Typography
          sx={{
            fontSize: 14.5,
            fontStyle: 'italic',
            color: colors.textSecondary,
            lineHeight: 1.7,
          }}
        >
          {renderInline(release.summary)}
        </Typography>
      </Box>

      {/* Show all / hide features toggle. */}
      <Box
        role="button"
        tabIndex={0}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen((v) => !v);
          }
        }}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.6,
          px: 1.5,
          py: 0.85,
          borderRadius: 999,
          cursor: 'pointer',
          fontSize: 12.5,
          fontWeight: 700,
          color: GOLD,
          bgcolor: `${GOLD}12`,
          border: `1px solid ${GOLD}33`,
          transition: 'background 0.15s, border-color 0.15s',
          '&:hover': { bgcolor: `${GOLD}20`, borderColor: `${GOLD}55` },
        }}
      >
        <KeyboardArrowDownIcon
          sx={{
            fontSize: 18,
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.2s',
          }}
        />
        {open
          ? 'Hide features'
          : `Show all ${featureCount} feature${featureCount === 1 ? '' : 's'}`}
      </Box>

      <Collapse in={open} unmountOnExit>
        <Box sx={{ mt: 2.5 }} />
        {/* Sections — a numbered list of changes; sub-points render as
          bullets inside each item's body. */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}>
          {release.sections.map((section, i) => {
            const shots = screenshotsOf(section);
            return (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  gap: 1.75,
                  borderTop: `1px solid ${colors.borderLight}`,
                  pt: 2.25,
                }}
              >
                {/* Number badge — gold gradient coin. */}
                <Box
                  sx={{
                    flexShrink: 0,
                    width: 27,
                    height: 27,
                    mt: 0.1,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 13,
                    fontWeight: 800,
                    fontFeatureSettings: '"tnum"',
                    color: '#fff',
                    background: `linear-gradient(140deg, ${GOLD_LIGHT}, ${GOLD})`,
                    boxShadow: `0 2px 6px ${GOLD}55, inset 0 1px 0 rgba(255,255,255,0.35)`,
                  }}
                >
                  {i + 1}
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{ fontSize: 15.5, fontWeight: 700, color: colors.textPrimary, mb: 0.75 }}
                  >
                    {section.title}
                  </Typography>
                  {renderBody(section.body)}
                  {shots.length > 0 && (
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns:
                          shots.length > 1 ? { xs: '1fr', sm: '1fr 1fr' } : '1fr',
                        gap: 1.5,
                        mt: 1.75,
                      }}
                    >
                      {shots.map((shot) => (
                        <Shot key={shot.src} shot={shot} onZoom={onZoom} />
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* Data notes / for-your-awareness */}
        {release.dataNotes && release.dataNotes.length > 0 && (
          <Box
            sx={{
              mt: 2.5,
              p: 2,
              borderRadius: 2,
              bgcolor: `${colors.warning}0d`,
              border: `1px solid ${colors.warning}40`,
            }}
          >
            <Typography
              sx={{
                fontSize: 10.5,
                fontWeight: 800,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: colors.warning,
                mb: 1,
              }}
            >
              For your awareness
            </Typography>
            {release.dataNotes.map((note, i) => (
              <Fragment key={i}>
                <Typography
                  sx={{ fontSize: 13.5, fontWeight: 700, color: colors.textPrimary, mb: 0.4 }}
                >
                  {renderInline(note.title)}
                </Typography>
                {renderBody(note.body)}
              </Fragment>
            ))}
          </Box>
        )}
      </Collapse>
    </Box>
  );
}

export function WhatsNewPage() {
  const [zoom, setZoom] = useState<string | null>(null);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const navigate = useNavigate();

  const latest = RELEASES[0];

  return (
    <SiteLayout subtitle="What's New" bgcolor={CREAM} showHome>
      <Container maxWidth="md" sx={{ py: { xs: 3.5, md: 6 } }}>
        {/* Back link */}
        <Box sx={{ mb: 2.5 }}>
          <Box
            role="button"
            tabIndex={0}
            onClick={() => navigate(-1)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') navigate(-1);
            }}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              cursor: 'pointer',
              fontSize: 13.5,
              fontWeight: 700,
              color: GOLD,
              '&:hover': { color: colors.goldDark, textDecoration: 'underline' },
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 18 }} /> Back
          </Box>
        </Box>

        {/* Changelog pill */}
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.6,
            px: 1.25,
            py: 0.5,
            mb: 2,
            borderRadius: 999,
            bgcolor: `${GOLD}14`,
            border: `1px solid ${GOLD}3a`,
            color: GOLD,
            fontSize: 11.5,
            fontWeight: 800,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
          }}
        >
          <AutoAwesomeIcon sx={{ fontSize: 15 }} /> Product changelog
        </Box>

        {/* Hero title */}
        <Typography
          sx={{
            fontSize: { xs: 32, md: 44 },
            fontWeight: 600,
            color: colors.brandDark,
            lineHeight: 1.14,
            letterSpacing: '-0.015em',
            mb: 1.75,
          }}
        >
          What&apos;s new in{' '}
          <Box component="span" sx={{ color: GOLD, fontWeight: 700 }}>
            Hooten Young
          </Box>{' '}
          Ops Platform
        </Typography>

        {/* Subtitle + tagline */}
        <Typography
          sx={{ fontSize: { xs: 15, md: 16.5 }, color: colors.textSecondary, lineHeight: 1.6 }}
        >
          A running log of{' '}
          <Box component="strong" sx={{ color: colors.textPrimary, fontWeight: 700 }}>
            product updates
          </Box>
          ,{' '}
          <Box component="strong" sx={{ color: colors.textPrimary, fontWeight: 700 }}>
            new features
          </Box>
          , and{' '}
          <Box component="strong" sx={{ color: colors.textPrimary, fontWeight: 700 }}>
            quality-of-life improvements
          </Box>{' '}
          we&apos;ve shipped
        </Typography>
        <Typography
          sx={{
            fontSize: { xs: 14, md: 15 },
            fontStyle: 'italic',
            color: GOLD,
            mt: 0.5,
            mb: 2.75,
          }}
        >
          to make the depletions and marketing dashboards easier to read at a glance.
        </Typography>

        {/* Latest chip */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 4.5 }}>
          <StatChip active>Latest · {latest?.version ?? '—'}</StatChip>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3.5 }}>
          {RELEASES.map((release, i) => (
            <ReleaseCard
              key={`${release.version}-${release.date}`}
              release={release}
              onZoom={setZoom}
              isLatest={i === 0}
            />
          ))}
        </Box>

        {/* Closer — always shipping / feedback CTA */}
        <Box
          sx={{
            mt: 5,
            p: { xs: 4, md: 6 },
            borderRadius: 4,
            textAlign: 'center',
            border: `1px solid ${GOLD}22`,
            background: `radial-gradient(circle, ${GOLD}1c 1px, transparent 1.4px) 0 0 / 18px 18px, linear-gradient(135deg, ${GOLD}12, #fffdf9 45%, ${GOLD}1e)`,
          }}
        >
          {/* Pill */}
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.6,
              px: 1.25,
              py: 0.5,
              mb: 2,
              borderRadius: 999,
              bgcolor: '#fff',
              border: `1px solid ${GOLD}3a`,
              color: GOLD,
              fontSize: 11.5,
              fontWeight: 800,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
            }}
          >
            <AutoAwesomeIcon sx={{ fontSize: 15 }} /> Shaped by your feedback
          </Box>

          <Typography
            sx={{
              fontSize: { xs: 26, md: 32 },
              fontWeight: 800,
              color: colors.brandDark,
              letterSpacing: '-0.02em',
              mb: 1.25,
            }}
          >
            More on the way
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: 15.5, md: 17 },
              fontWeight: 700,
              color: colors.textPrimary,
              mb: 1,
            }}
          >
            Most of what&apos;s on this page started as a single piece of feedback.
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: 13.5, md: 14.5 },
              color: colors.textSecondary,
              lineHeight: 1.65,
              maxWidth: 560,
              mx: 'auto',
            }}
          >
            If there&apos;s{' '}
            <Box component="strong" sx={{ color: GOLD, fontWeight: 700 }}>
              a number you&apos;re tracking
            </Box>
            ,{' '}
            <Box component="strong" sx={{ color: GOLD, fontWeight: 700 }}>
              a view you wish existed
            </Box>
            , or{' '}
            <Box component="strong" sx={{ color: GOLD, fontWeight: 700 }}>
              a workflow that could be sharper
            </Box>{' '}
            — tell us.
          </Typography>

          <Typography sx={{ fontSize: 13.5, fontStyle: 'italic', color: GOLD, mt: 1, mb: 3 }}>
            The next release is built from what we hear.
          </Typography>

          {/* Feedback CTA — opens the feedback dialog. */}
          <Box
            role="button"
            tabIndex={0}
            onClick={() => setFeedbackOpen(true)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setFeedbackOpen(true);
              }
            }}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.9,
              px: 2.5,
              py: 1.25,
              borderRadius: 999,
              cursor: 'pointer',
              color: '#fff',
              fontSize: 14,
              fontWeight: 700,
              background: `linear-gradient(140deg, ${GOLD_LIGHT}, ${GOLD})`,
              boxShadow: `0 8px 22px -6px ${GOLD}88`,
              transition: 'transform 0.15s, box-shadow 0.15s',
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: `0 12px 28px -6px ${GOLD}aa`,
              },
            }}
          >
            <RecordVoiceOverIcon sx={{ fontSize: 19 }} /> Share your feedback
          </Box>
        </Box>
      </Container>

      <FeedbackDialog open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />

      {/* Lightbox */}
      <Dialog
        open={zoom !== null}
        onClose={() => setZoom(null)}
        maxWidth="lg"
        slotProps={{ paper: { sx: { bgcolor: 'transparent', boxShadow: 'none', m: 2 } } }}
      >
        <IconButton
          onClick={() => setZoom(null)}
          sx={{
            position: 'absolute',
            top: 4,
            right: 4,
            color: '#fff',
            bgcolor: 'rgba(15,23,42,0.55)',
            '&:hover': { bgcolor: 'rgba(15,23,42,0.75)' },
          }}
        >
          <CloseIcon />
        </IconButton>
        {zoom && (
          <Box
            component="img"
            src={zoom}
            alt="Screenshot"
            sx={{ display: 'block', maxWidth: '100%', maxHeight: '88vh', borderRadius: 2 }}
          />
        )}
      </Dialog>
    </SiteLayout>
  );
}
